import { create } from 'zustand';
import type { ClientBooking, ClientBookingItem, ConfirmationModel } from '../types/client';

/** Minimal shape required by seedFromRebook — both ClientBooking and ClientBookingItem satisfy this */
type RebookSource = Pick<ClientBooking | ClientBookingItem, 'providerId' | 'serviceId'>;

interface BookingFlowState {
  providerId: number | null;
  providerDisplayName: string | null;
  serviceId: number | null;
  selectedDate: string | null;   // YYYY-MM-DD
  selectedTime: string | null;   // HH:mm
  confirmationModel: ConfirmationModel | null;
  isSubmitting: boolean;
  submitError: string | null;

  setProvider: (id: number, displayName: string) => void;
  setProviderDisplayName: (displayName: string) => void;
  setService: (id: number, confirmationModel: ConfirmationModel) => void;
  setDateTime: (date: string, time: string) => void;
  setSubmitting: (v: boolean) => void;
  setSubmitError: (msg: string | null) => void;
  reset: () => void;
  resetFlow: () => void; // alias for reset (backwards compat)
  seedFromRebook: (booking: RebookSource) => void;
}

const initialFlow = {
  providerId: null as number | null,
  providerDisplayName: null as string | null,
  serviceId: null as number | null,
  selectedDate: null as string | null,
  selectedTime: null as string | null,
  confirmationModel: null as ConfirmationModel | null,
  isSubmitting: false,
  submitError: null as string | null,
};

export const useBookingFlowStore = create<BookingFlowState>((set) => ({
  ...initialFlow,

  // setProvider cascades: resets service, date, time, confirmationModel
  setProvider: (id, displayName) =>
    set({
      providerId: id,
      providerDisplayName: displayName,
      serviceId: null,
      selectedDate: null,
      selectedTime: null,
      confirmationModel: null,
    }),

  setProviderDisplayName: (displayName) => set({ providerDisplayName: displayName }),

  // setService cascades: resets date, time
  setService: (id, confirmationModel) =>
    set({
      serviceId: id,
      confirmationModel,
      selectedDate: null,
      selectedTime: null,
    }),

  setDateTime: (date, time) => set({ selectedDate: date, selectedTime: time }),

  setSubmitting: (v) => set({ isSubmitting: v }),
  setSubmitError: (msg) => set({ submitError: msg }),

  reset: () => set(initialFlow),
  resetFlow: () => set(initialFlow), // alias

  seedFromRebook: (booking) =>
    set({
      ...initialFlow,
      providerId: booking.providerId,
      serviceId: booking.serviceId,
    }),
}));

// Selector — canAdvance per step (derived, not stored)
export function selectCanAdvance(
  state: BookingFlowState,
  step: number,
  providerCount: number,
): boolean {
  const { providerId, serviceId, selectedTime } = state;

  if (providerCount > 1) {
    if (step === 0) return providerId !== null;
    if (step === 1) return serviceId !== null;
    if (step === 2) return selectedTime !== null;
    return false; // confirm step — MainButton handles submit directly
  } else {
    if (step === 0) return serviceId !== null;
    if (step === 1) return selectedTime !== null;
    return false; // confirm step
  }
}
